import React, { MouseEvent, useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { Styles } from '@t2c/shared-fe';
import { Iconify, NextImage } from '@sdf/base';
import { DropdownPopover } from './DropdownPopover';
import { DropdownMenuAction, IAudioCreation, Status } from '../../@types';
import ReactPlayer from 'react-player';
import { useAudioPlayer, usePopover, useTextToSpeechCard } from '../../hooks';
import IconButton from '@mui/material/IconButton';
import { StatusWithProgressBar } from '../../Common/StatusWithProgressBar';
import { extractDate, extractTime } from '../../utils';
import { ProgressStuckNote } from '../../Common';


interface TextToSpeechCardProps {
  data: IAudioCreation;
  menuActions: DropdownMenuAction[];
  isSmallSize?: boolean;
  isHideName?: boolean;
  isActive?: boolean;
}

export const TextToSpeechCard: React.FC<TextToSpeechCardProps> = ({
  data,
  menuActions,
  isSmallSize,
  isHideName,
  isActive = false,
}) => {
  const {
    isSeekBarVisible,
    isPlaying,
    playbackPosition,
    togglePlayPause,
    handleSeekChange,
    handleProgress,
    handleEnded,
    playerRef,
  } = useAudioPlayer();

  const { popoverAnchor, handleOpenPopover, handleClosePopover } = usePopover();

  const { name, updatedAt } = data || {};

  const { audioDataStatus, cardProgress, audioUrl } = useTextToSpeechCard({
    textToSpeechData: data,
  });

  return (
    <Card sx={{
      ...styles.card, 
      borderWidth: isActive ? 2 : 1,
      borderColor: isActive ? 'text.coloredText' : 'background.border',
    }}>
      {(audioDataStatus === Status.InProgress ||
        audioDataStatus === Status.NotStarted ) && (
        <Box sx={styles.infoButton}>
          <ProgressStuckNote showToolTip={true}/>
        </Box>
      )}

      {!!menuActions.length && audioDataStatus !== Status.InProgress && (
        <Box sx={styles.menuButton} onClick={handleOpenPopover}>
          <Iconify
            className="audio-menu-icon"
            icon="solar:menu-dots-bold"
            fontSize={'22px'}
            sx={styles.menuIcon}
          />
        </Box>
      )}

      <Stack p={2} mt={2}>
        <Box mb={0.5}>
          <NextImage
            src={isPlaying ? '/assets/equiliser.gif' : '/assets/still_equiliser.png'}
            alt="Audio Visual"
            width={600}
            height={40}
          />
        </Box>
        <Stack sx={styles.playerControls}>
          <Box>
            <IconButton
              onClick={togglePlayPause}
              sx={{ p: 0 }}
              disabled={audioDataStatus === Status.InProgress || audioDataStatus === Status.Error}
            >
              <Iconify
                icon={isPlaying ? 'fa-solid:pause-circle' : 'fa-solid:play-circle'}
                width={isSmallSize ? 30 : 36}
                height={isSmallSize ? 30 : 36}
                sx={styles.playPauseIcon}
              />
            </IconButton>
          </Box>
          {isSeekBarVisible && (
            <Slider
              value={playbackPosition * 100}
              onChange={handleSeekChange}
              aria-labelledby="audio-slider"
              sx={styles.slider}
            />
          )}
        </Stack>
      </Stack>

      {(audioDataStatus === Status.InProgress ||
        audioDataStatus === Status.NotStarted ||
        audioDataStatus === Status.Error) && (
        <Stack
          sx={{
            width: '40%',
            position: 'absolute',
            top: '70%',
            left: '30%',
          }}
        >
          <StatusWithProgressBar
            progressPercentage={cardProgress}
            status={audioDataStatus}
            isBrollCaption
          />
        </Stack>
      )}

      {!isHideName && (
        <Box className="audio-title-container" sx={styles.titleContainer}>
          <Tooltip title={name}>
            <Typography sx={styles.title}>{!isHideName ? name : ''}</Typography>
          </Tooltip>
          <Typography sx={{fontSize: 12,textAlign: 'right'}}>
            {extractDate(updatedAt)}
            &nbsp;
            {extractTime(updatedAt)}
          </Typography>
       </Box>
      )}

      <ReactPlayer
        ref={playerRef}
        url={audioUrl}
        playing={isPlaying}
        onProgress={handleProgress}
        onEnded={handleEnded}
        height="0"
        width="0"
        onError={(error, data) => {
          console.error('Audio playback error:', error, data);
        }}
        progressInterval={100} // Adjust as needed
      />
      <DropdownPopover
        anchorElement={popoverAnchor}
        menuActions={menuActions}
        onClose={handleClosePopover}
        data={data}
      />
    </Card>
  );
};

const styles: Styles = {
  card: {
    width: '100%',
    cursor: 'default',
    zIndex: 'auto',
    border: '1px solid',
    overflow: 'hidden',
    borderColor: (theme) => theme.palette.background.border,
    background: (theme) => theme.palette.background.secondary,
    position: 'relative',
    color: (theme) => theme.palette.text.primary,
    '&:hover': {
      background: (theme) => theme.palette.background.primary,
      '.audio-title-container': {
        background: (theme) => theme.palette.background.activeBorder,
      },
    },
  },
  menuButton: {
    borderRadius: '4px',
    position: 'absolute',
    top: '10px',
    right: '8px',
    padding: '4px',
    cursor: 'pointer',
    '&:hover': {
    },
  },
  infoButton: {
    borderRadius: '4px',
    position: 'absolute',
    top: '10px',
    left: '8px',
    padding: '4px',
    cursor: 'pointer',
    '&:hover': {
    },
  },
  playPauseIcon: {
    color: (theme) => theme.palette.primary.main,
    borderRadius: 1000,

    cursor: 'pointer',
    '&:hover': {
      boxShadow: (theme) => theme.shadows[4],
    },
  },
  playerControls: {
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: 2,
  },
  slider: {
    width: '100%',
    height: 8,
    '& .MuiSlider-thumb': {
      width: 20,
      height: 20,
      backgroundColor: '#fff',
      '&:before': {
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
      },
    },
    '& .MuiSlider-track': {
      border: 'none',
      backgroundColor: (theme) => theme.palette.primary.main,
    },
    '& .MuiSlider-rail': {
      opacity: 1,
      backgroundColor: '#fff',
    },
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 0.8,
  },
  title: {
    fontSize: 14,
    fontWeight: 400,
    maxWidth: '50%',
    textAlign: 'left',
    color: 'inherit',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  menuIcon: {
    color: (theme) => theme.palette.text.primary,
  },
  statusWrapper: {
    position: 'absolute',
    width: '100%',
    top: '50%',
    alignItems: 'center',
  },
  statusContainer: {
    maxWidth: 300,
    padding: 0.3,
    paddingX: 2,
    borderRadius: 25,
  },
};
